import {
  JourneyDefinition,
  StageDefinition,
  type ConfigKind,
  type ValidationIssue,
} from '@cyberscore/contracts';

/**
 * The journey builder's logic (task 41), apart from the screen so it can be tested. A journey lists stages
 * with their prerequisites; each stage is its own config item with its questions. Edits are kept as
 * drafts in the browser until saved; the core checks shapes on save and the whole set on validate.
 */

export type Journey = JourneyDefinition;
export type Stage = StageDefinition;
export type JourneyStage = Journey['stages'][number];

/** Who authors which scopes: the platform its own, country and industry; a tenant the narrower ones. */
export const PLATFORM_LEVELS = ['platform', 'country', 'industry'];
export const TENANT_LEVELS = ['tenant', 'product', 'customer'];

export interface ItemSummary {
  id: string;
  kind: ConfigKind;
  key: string;
  scope_level: string;
  scope_key: string;
  revision: number;
  title: string;
  updated_at: string;
  retired_at: string | null;
}

export function ownLevels(platformTenant: boolean): string[] {
  return platformTenant ? PLATFORM_LEVELS : TENANT_LEVELS;
}

/**
 * The variant of a key the builder works on: the author's own (narrowest first) if there is one, otherwise
 * what they inherit from the platform.
 */
export function pickVariant<T extends Pick<ItemSummary, 'key' | 'scope_level'>>(
  items: readonly T[],
  key: string,
  own: readonly string[],
): T | undefined {
  const all = items.filter((i) => i.key === key);
  const mine = all.filter((i) => own.includes(i.scope_level));
  const rank = (i: T) => [...PLATFORM_LEVELS, ...TENANT_LEVELS].indexOf(i.scope_level);
  return [...(mine.length ? mine : all)].sort((a, b) => rank(b) - rank(a))[0];
}

/** The stages that wait for this one, directly or through others: the ones its routes may skip (task 85). */
export function laterStages(j: Pick<Journey, 'stages'>, key: string): string[] {
  const out = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of j.stages)
      if (!out.has(s.stage) && s.stage !== key && s.after.some((a) => a.stage === key || out.has(a.stage))) {
        out.add(s.stage);
        grew = true;
      }
  }
  return j.stages.map((s) => s.stage).filter((k) => out.has(k));
}

/** Stages laid out in columns: a stage sits one column after the latest stage it waits for. */
export function stageColumns(j: Pick<Journey, 'stages'>): string[][] {
  const level = new Map<string, number>();
  const known = new Set(j.stages.map((s) => s.stage));
  for (const s of j.stages) {
    const deps = s.after.filter((a) => known.has(a.stage) && level.has(a.stage));
    level.set(s.stage, deps.length ? Math.max(...deps.map((a) => level.get(a.stage)!)) + 1 : 0);
  }
  const cols: string[][] = [];
  for (const s of j.stages) (cols[level.get(s.stage)!] ??= []).push(s.stage);
  return cols.filter(Boolean);
}

export interface Problem {
  /** `journey`, or the stage key it is about. */
  where: string;
  message: string;
}

/**
 * What is wrong with the draft right now, before saving: shapes, stages that wait for something later or
 * missing, questions asked twice or unknown. The core's validate checks the whole set again.
 */
export function journeyProblems(
  journey: unknown,
  stages: Readonly<Record<string, unknown>>,
  questionKeys: ReadonlySet<string>,
): Problem[] {
  const out: Problem[] = [];
  const j = JourneyDefinition.safeParse(journey);
  if (!j.success) {
    for (const i of j.error.issues) out.push({ where: 'journey', message: `${i.path.join('.') || 'journey'}: ${i.message}` }); // prettier-ignore
    return out;
  }
  const seen = new Set<string>();
  const askedIn = new Map<string, string>();
  j.data.stages.forEach((s) => {
    if (seen.has(s.stage)) out.push({ where: s.stage, message: `${s.stage} is in the journey twice` });
    for (const a of s.after)
      if (!seen.has(a.stage))
        out.push({
          where: s.stage,
          message: j.data.stages.some((x) => x.stage === a.stage)
            ? `${s.stage} waits for ${a.stage}, which comes later: move it above`
            : `${s.stage} waits for ${a.stage}, which is not in this journey`,
        });
    seen.add(s.stage);
    const body = stages[s.stage];
    if (body === undefined) return out.push({ where: s.stage, message: `No stage ${s.stage} exists` });
    const st = StageDefinition.safeParse(body);
    if (!st.success) {
      for (const i of st.error.issues) out.push({ where: s.stage, message: `${i.path.join('.') || 'stage'}: ${i.message}` }); // prettier-ignore
      return;
    }
    if (st.data.mode === 'conditional' && !st.data.entry)
      out.push({ where: s.stage, message: 'A conditional stage needs an entry condition' });
    if (!st.data.questions.length) out.push({ where: s.stage, message: 'Asks no questions yet' });
    for (const q of st.data.questions) {
      if (questionKeys.size && !questionKeys.has(q))
        out.push({ where: s.stage, message: `Unknown question ${q}` });
      const other = askedIn.get(q);
      if (other && other !== s.stage) out.push({ where: s.stage, message: `${q} is also asked in ${other}` });
      askedIn.set(q, s.stage);
    }
  });
  return out;
}

/** The core's validation issues that concern this journey or its stages. */
export function relevantIssues(
  issues: readonly ValidationIssue[],
  journeyKey: string,
  stageKeys: readonly string[],
) {
  return issues.filter((i) => (i.kind === 'journey' && i.key === journeyKey) || (i.kind === 'stage' && stageKeys.includes(i.key))); // prettier-ignore
}

export function move<T>(list: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return [...list];
  const out = [...list];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
}

/** Moving a stage keeps prerequisites valid: anything it waited for that is now after it is dropped. */
export function moveStage(j: Journey, from: number, to: number): Journey {
  const stages = move(j.stages, from, to);
  return {
    ...j,
    stages: stages.map((s, i) => ({
      ...s,
      after: s.after.filter((a) => stages.slice(0, i).some((x) => x.stage === a.stage)),
    })),
  };
}

/** Removing a stage also removes it from what the others wait for. */
export function removeStage(j: Journey, key: string): Journey {
  return {
    ...j,
    stages: j.stages.filter((s) => s.stage !== key).map((s) => ({ ...s, after: s.after.filter((a) => a.stage !== key) })), // prettier-ignore
  };
}

/** A new stage waits for the last one by default, so the journey stays sequential unless told otherwise. */
export function addStage(j: Journey, key: string): Journey {
  const last = j.stages.at(-1);
  return {
    ...j,
    stages: [...j.stages, { stage: key, after: last ? [{ stage: last.stage, when: 'completed' }] : [] }],
  };
}

/** Sets (or clears, with null) whether `key` waits for `on`, and for what. */
export function setPrerequisite(
  j: Journey,
  key: string,
  on: string,
  when: 'completed' | 'started' | null,
): Journey {
  return {
    ...j,
    stages: j.stages.map(
      (s) =>
      s.stage !== key
        ? s
        : { ...s, after: when === null ? s.after.filter((a) => a.stage !== on) : [...s.after.filter((a) => a.stage !== on), { stage: on, when }] }, // prettier-ignore
    ),
  };
}

/** "Waits for S01 and S02 to be completed, S03 to be started", or "Starts straight away". */
export function describeWait(s: JourneyStage, name: (key: string) => string): string {
  if (!s.after.length) return 'Starts straight away';
  const done = s.after.filter((a) => a.when === 'completed').map((a) => name(a.stage));
  const started = s.after.filter((a) => a.when === 'started').map((a) => name(a.stage));
  const list = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}` : xs[0]);
  return `After ${[done.length && `${list(done)} ${done.length > 1 ? 'are' : 'is'} completed`, started.length && `${list(started)} ${started.length > 1 ? 'have' : 'has'} started`].filter(Boolean).join('; ')}`; // prettier-ignore
}

export function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
